import { Mic } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Assistant } from "../../api";
import { useUpdateAssistant } from "../../hooks";
import SettingsSheet from "./SettingsSheet";
import { Field, ToggleRow, SliderRow } from "./controls";
import { LANGUAGES } from "./options";
import ProviderPicker from "./ProviderPicker";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assistant: Assistant;
};

/** Transcriber (speech-to-text) settings drawer. */
export default function TranscriberSettings({ open, onOpenChange, assistant }: Props) {
  const updateMut = useUpdateAssistant(assistant.id);

  return (
    <SettingsSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Transcriber"
      description="Speech-to-text that turns caller audio into text for the model."
      icon={<Mic className="size-4 text-emerald-500" />}
    >
      <ProviderPicker
        kind="transcriber"
        assistant={assistant}
        current={assistant.transcriber}
        patchKey="transcriber_id"
        optionLabel="Model"
      />

      <Field label="Language">
        <Select
          value={assistant.transcriber_language}
          onValueChange={(l) => updateMut.mutate({ transcriber_language: l })}
          disabled={updateMut.isPending}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((l) => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <div className="space-y-4 border-t pt-4">
        <ToggleRow
          label="Background denoising"
          description="Filter background noise while the caller is speaking."
        />
        <ToggleRow
          label="Use numerals"
          description="Write numbers as digits (e.g. 25) instead of words."
          defaultChecked
        />
        <ToggleRow
          label="Profanity filter"
          description="Mask profanity in the transcript with asterisks."
        />
      </div>

      <div className="border-t pt-4">
        <SliderRow
          label="Confidence threshold"
          description="Drop transcript segments below this confidence score."
          min={0}
          max={1}
          step={0.05}
          defaultValue={0.4}
          minLabel="0"
          maxLabel="1.0"
          format={(v) => v.toFixed(2)}
        />
      </div>

      <div className="border-t pt-4">
        <ToggleRow
          label="Auto fallback"
          description="Switch to a backup transcriber if the primary one fails."
          defaultChecked
        />
      </div>
    </SettingsSheet>
  );
}
